import {
  take,
  select,
  call,
  fork,
  cancel,
  put,
  takeLatest,
  delay
} from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import { selectors } from "reducers";
import { Types } from "actions/twilio-chat";
import { Client } from "@twilio/conversations";
import { Types as AuthTypes } from "actions/authentication";
import { isLogoutResponse, isUnauthorizedResponse } from "sagas/authentication";

export default function* rootTwilioChatSaga() {
  while (true) {
    yield take(Types.INITIALIZE_TWILIO_CHAT_CLIENT);
    try {
      const chatClient = yield createTwilioChatClient();
      yield put({ type: Types.TWILIO_CHAT_READY });
      const watchStartChatTask = yield fork(watchStartChat, chatClient);
      yield take([
        AuthTypes.LOGOUT_USER_INACTIVE,
        AuthTypes.LOGIN_USER_RESPONSE,
        isLogoutResponse,
        isUnauthorizedResponse
      ]);

      if (watchStartChatTask) yield cancel(watchStartChatTask);
    } catch (error) {
      console.error(error);
    }
  }
}

function* createTwilioChatClient() {
  const twilioToken = yield select(state => selectors.getTwilioToken(state));
  const client = yield Client.create(twilioToken);
  return client;
}

function* watchStartChat(chatClient) {
  yield takeLatest(Types.START_TWILIO_CHAT, function* (action) {
    const { payload: conversationSid } = action;
    yield startTwilioChat(conversationSid, chatClient);
  });
}

function* startTwilioChat(conversationSid, chatClient) {
  try {
    yield put({
      type: Types.TWILIO_CHAT_MESSAGE_ADDED,
      payload: { body: "Connecting to Chat Room" }
    });
    const conversation = yield getTwilioConversation(
      chatClient,
      conversationSid
    );
    if (!conversation) {
      yield put({
        type: Types.TWILIO_CHAT_ERROR,
        error: "Could not connect to chat room"
      });
      throw Error("could not get conversation");
    }
    if (conversation.status !== "joined") {
      yield conversation.join();
    }
    yield call(getChatHistory, conversation);
    yield put({ type: Types.TWILIO_CHAT_STARTED });
    yield put({
      type: Types.TWILIO_CHAT_MESSAGE_ADDED,
      payload: { body: "Connected To Chat Room" }
    });

    const handlePageLeave = () => {
      conversation.leave();
    };
    window.addEventListener("beforeunload", handlePageLeave);
    const conversationEventsTask = yield fork(
      handleConversationEvents,
      conversation
    );
    yield take([
      Types.CLOSE_TWILIO_CHAT,
      Types.START_TWILIO_CHAT,
      Types.END_TWILIO_CHAT
    ]);
    yield conversation.leave();
    if (conversationEventsTask) yield cancel(conversationEventsTask);
    yield put({ type: Types.TWILIO_CHAT_ENDED });
    window.removeEventListener("beforeunload", handlePageLeave);
  } catch (error) {
    console.error(error);
    yield put({ type: Types.TWILIO_CHAT_ERROR, error });
    yield put({ type: Types.TWILIO_CHAT_ENDED, error });
  }
}

async function getTwilioConversation(chatClient, conversationSid) {
  try {
    const conversation = await chatClient.getConversationBySid(conversationSid);
    if (conversation) return conversation;
  } catch (error) {
    console.error("getTwilioConversation ERROR:", error);
    throw error;
  }
}

function* getChatHistory(conversation) {
  const { items = [] } = yield conversation.getMessages();
  const restoredMessages = items.map(message => ({
    sid: message.sid,
    body: message.body,
    author: message.state?.author || "",
    dateCreated: message.dateCreated,
    timestamp: message.dateCreated,
    index: message.index,
    attributes: message.attributes || {}
  }));
  yield put({
    type: Types.TWILIO_CHAT_RESTORE_MESSAGES,
    payload: restoredMessages
  });
}
const conversationEventChannel = (conversation, userId) =>
  eventChannel(emit => {
    const messageAddedHandler = message => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: {
          sid: message.sid,
          body: message.body,
          author: message.state?.author || "",
          dateCreated: message.dateCreated,
          timestamp: message.dateCreated,
          index: message.index,
          attributes: message.attributes || {}
        }
      });
    };

    const participantJoinedHandler = () => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "User Joined Chat" }
      });
    };

    const participantLeftHandler = () => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "User Left Chat" }
      });
    };

    const removedHandler = () => {
      emit({
        type: Types.TWILIO_CHAT_MESSAGE_ADDED,
        payload: { body: "Chat Ended" }
      });
      emit({
        type: Types.END_TWILIO_CHAT
      });
    };

    const typingStartedHandler = participant => {
      emit({
        type: Types.TWILIO_CHAT_TYPING_STARTED,
        payload: { identity: participant.identity }
      });
    };

    const typingEndedHandler = participant => {
      emit({
        type: Types.TWILIO_CHAT_TYPING_ENDED,
        payload: { identity: participant.identity }
      });
    };

    const participantUpdatedHandler = ({ participant, updateReasons }) => {
      if (participant.identity !== userId && updateReasons.includes("lastReadMessageIndex")) 
        {
        emit({
          type: Types.TWILIO_CHAT_PARTICIPANT_UPDATED,
          payload: {
            msgIndex: participant.state.lastReadMessageIndex,
            timestamp: participant.state.lastReadTimestamp?.toISOString() || null,
            participantIdentity: participant.identity
          }
        });
      }
    };

    conversation.on("messageAdded", messageAddedHandler);
    conversation.on("participantJoined", participantJoinedHandler);
    conversation.on("participantLeft", participantLeftHandler);
    conversation.on("removed", removedHandler);
    conversation.on("typingStarted", typingStartedHandler);
    conversation.on("typingEnded", typingEndedHandler);
    conversation.on("participantUpdated", participantUpdatedHandler);

    return () => {
      conversation.off("messageAdded", messageAddedHandler);
      conversation.off("participantJoined", participantJoinedHandler);
      conversation.off("participantLeft", participantLeftHandler);
      conversation.off("removed", removedHandler);
      conversation.off("typingStarted", typingStartedHandler);
      conversation.off("typingEnded", typingEndedHandler);
      conversation.off("participantUpdated", participantUpdatedHandler);
    };
  });

function* handleConversationEvents(conversation) {
  const userName = yield select(state => selectors.getUserName(state));
  const userId = yield select(state => selectors.getUserId(state));

  yield fork(listenSendMessage, conversation, userName);
  yield fork(listenUserTyping, conversation);
  yield fork(listenUpdateReadHorizon, conversation);
  const conversationActions = yield call(
    conversationEventChannel,
    conversation,
    userId
  );
  try {
    while (true) {
      const action = yield take(conversationActions);
      yield put(action);
    }
  } finally {
    conversationActions.close();
  }
}

function* listenSendMessage(conversation, userName) {
  while (true) {
    const { payload } = yield take(Types.TWILIO_SEND_MESSAGE);
    try {
      yield conversation.sendMessage(payload, {
        authorName: userName
      });
    } catch (error) {
      console.error(error);
    }
  }
}

function* listenUserTyping(conversation) {
  let lastTypingTime = 0;
  let trailingTask = null;
  const throttleDelay = 3000;

  while (true) {
    yield take(Types.TWILIO_CHAT_USER_TYPING);
    const now = Date.now();
    const timeSinceLastCall = now - lastTypingTime;

    if (timeSinceLastCall >= throttleDelay) {
      lastTypingTime = now;
      if (trailingTask) {
        yield cancel(trailingTask);
        trailingTask = null;
      }
      try {
        yield conversation.typing();
      } catch (error) {
        console.error(error);
      }
    } else {
      if (trailingTask) yield cancel(trailingTask);
      trailingTask = yield fork(function* () {
        try {
          yield delay(throttleDelay - timeSinceLastCall);
          lastTypingTime = Date.now();
          yield conversation.typing();
        } catch (error) {
          console.error(error);
        }
      });
    }
  }
}

function* listenUpdateReadHorizon(conversation) {
  while (true) {
    yield take(Types.TWILIO_CHAT_UPDATE_READ_HORIZON);
    try {
      const messages = yield select(state => selectors.getChatMessages(state));
      const userId = yield select(state => selectors.getUserId(state));

      if (messages.length > 0) {
        const lastReceivedMessage = [...messages]
          .reverse()
          .find(msg => msg.author !== userId);

        if (lastReceivedMessage && lastReceivedMessage.index !== undefined) {
          yield conversation.updateLastReadMessageIndex(
            lastReceivedMessage.index
          );
        }
      }
    } catch (error) {
      console.error("Failed to update read horizon:", error);
    }
  }
}
